import React, { useState, useEffect, useMemo } from 'react';
import DataTable from '../components/common/DataTable';
import { DataForm } from '../components/common/DataForm';
import SearchBar from '../components/common/SearchBar';
import PageHeader from '../components/common/PageHeader';
import Pagination from '../components/common/Pagination';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import useDebouncedSearch from '../hooks/useDebouncedSearch';
import { Hash, Newspaper, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';

const MOCK_DATA = [
  { id: 1, title: 'National Level Tournament Announced for October', category: 'Official Event', date: '2024-05-10', content: 'Details of tournament rules and regulations.' },
  { id: 2, title: 'New State Committee Formed in Delhi NCR', category: 'Breaking News', date: '2024-05-14', content: 'Members elected and announcements made.' }
];

const COLUMNS = [
  { header: 'Headline', accessor: 'title' },
  { header: 'Type', accessor: 'category' },
  { header: 'Publish Date', accessor: 'date' }
];

const FORM_FIELDS = [
  { label: 'Featured Image', accessor: 'image', type: 'file', fullWidth: true },
  { label: 'News Category', accessor: 'category', type: 'select', options: [
    { label: 'Breaking News', value: 'Breaking News' },
    { label: 'Official Event', value: 'Official Event' },
    { label: 'Press Conference', value: 'Press Conference' }
  ]},
  { label: 'Headline Title', accessor: 'title', placeholder: 'Enter news headline' },
  { label: 'Publish Date', accessor: 'date', type: 'date' },
  { label: 'Full Story Content', accessor: 'content', type: 'textarea', placeholder: 'Write full news story here...', fullWidth: true }
];

export default function News() {
  const [dataList, setDataList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingData, setEditingData] = useState(null);
  
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Search, Filter & Pagination State
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const { searchQuery, setSearchQuery, debouncedQuery } = useDebouncedSearch(() => setCurrentPage(1));
  const itemsPerPage = 5;

  const fetchData = async () => {
    setIsLoading(true);
    setTimeout(() => {
      setDataList(MOCK_DATA);
      setIsLoading(false);
    }, 500);
  };

  useEffect(() => {
    fetchData();
  }, []);



  const filteredData = useMemo(() => {
    let result = dataList;
    if (categoryFilter !== 'All') {
      result = result.filter(item => item.category === categoryFilter);
    }
    if (debouncedQuery) {
      result = result.filter(item => 
        Object.values(item).some(val => 
          String(val).toLowerCase().includes(debouncedQuery.toLowerCase())
        )
      );
    }
    return result;
  }, [dataList, debouncedQuery, categoryFilter]);

  const kpis = useMemo(() => {
    const total = dataList.length;
    return [
      { label: "Total Articles", value: total, icon: <Newspaper size={20} />, color: 'blue' },
      { label: "Breaking Updates", value: dataList.filter(d => d.category === 'Breaking News').length, icon: <Hash size={20} />, color: 'orange' },
      { label: "Official Events", value: dataList.filter(d => d.category === 'Official Event').length, icon: <Calendar size={20} />, color: 'green' }
    ];
  }, [dataList]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleFormSubmit = async (formData) => {
    if (editingData) {
      setDataList(prev => prev.map(item => item.id === editingData.id ? { ...item, ...formData } : item));
      toast.success('News article updated successfully!');
    } else {
      const newItem = { ...formData, id: Date.now() };
      setDataList(prev => [newItem, ...prev]);
      toast.success('News article published successfully!');
    }
    setIsFormOpen(false);
    setEditingData(null);
  };

  const handleEdit = (row) => {
    setEditingData(row);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (row) => {
    setDeleteTarget(row);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteExecute = async () => {
    if (!deleteTarget) return;
    setIsDeleteModalOpen(false);
    setDataList(prev => prev.filter(item => item.id !== deleteTarget.id));
    toast.success('News article deleted successfully!');
    setDeleteTarget(null);
  };

  return (
    <div className="page-content">
      <PageHeader 
        title="News & Press Media"
        subtitle="Publish media announcements, news articles, and press briefings"
        onAdd={() => { setEditingData(null); setIsFormOpen(true); }}
        addLabel="Publish Article"
        kpis={kpis}
      />
      
      {isFormOpen && (
        <DataForm 
          title={editingData ? `Edit News Article` : `Publish New Article`} 
          fields={FORM_FIELDS} 
          initialData={editingData}
          onSubmit={handleFormSubmit}
          onClose={() => setIsFormOpen(false)}
        />
      )}
      
      <div className="table-card-container">
        <div className="table-actions-area">
          <div className="table-actions-left">
            <SearchBar 
              placeholder="Search headlines..."
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>

          <div className="table-actions-right">
            <div className="filter-select-wrapper">
              <select 
                className="table-filter-select"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="All">All Categories</option>
                <option value="Breaking News">Breaking News</option>
                <option value="Official Event">Official Event</option>
                <option value="Press Conference">Press Conference</option>
              </select>
            </div>
          </div>
        </div>

        <div className="table-responsive-wrapper">
          <DataTable 
            columns={COLUMNS} 
            data={paginatedData} 
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            loading={isLoading}
          />
        </div>

        <Pagination 
          currentPage={currentPage}
          totalPages={totalPages}
          totalEntries={filteredData.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeleteTarget(null); }}
        onConfirm={handleDeleteExecute}
        title="Delete News Article"
        message="Are you sure you want to delete this published news article?"
        itemName={deleteTarget ? deleteTarget.title : ''}
      />
    </div>
  );
}
